import pixelsApi from 'api/requests/pixels'
import Button from 'components/_UI/Button'
import Fieldset from 'components/_UI/Fieldset'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import Switch from 'components/_UI/Switch'
import { useEvent } from 'context/events/Event'
import fieldValidations from 'helpers/fieldValidations'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { AlertCircle, Check, Crosshair, ShieldCheck } from 'lucide-react'
import { Column } from 'styled/alignment/Column'
import { FullyCentered } from 'styled/alignment/Center'
import { StyledLabel } from 'styled/UI/StyledForm'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import platforms, { findPlatform } from './platforms'

const submitErrorMessage = 'Não foi possível salvar o pixel. Verifique os dados e tente novamente.'

function PixelForm({ visible, setVisible, onSuccess, edit = false }) {
    const { event } = useEvent()
    const [loading, setLoading] = useState(false)
    const [active, setActive] = useState(edit ? edit.is_active : true)
    const [selectedPlatform, setSelectedPlatform] = useState(edit ? findPlatform(edit.platform) : null)
    const [platformError, setPlatformError] = useState(false)
    const [selectedEvents, setSelectedEvents] = useState(edit ? (edit.tracking_events || []) : [])
    const [trackingEvents, setTrackingEvents] = useState([])
    const [loadingEvents, setLoadingEvents] = useState(true)
    const [submitError, setSubmitError] = useState('')
    const [removeToken, setRemoveToken] = useState(false)

    const supportsConversionApi = Boolean(selectedPlatform?.supportsConversionApi)
    const allSelected = trackingEvents.length > 0 && selectedEvents.length === trackingEvents.length

    useEffect(() => {
        pixelsApi.getTrackingEvents()
            .then(({ data }) => setTrackingEvents(data?.data || []))
            .catch(() => {})
            .finally(() => setLoadingEvents(false))
    }, [])

    const {
        register,
        handleSubmit,
        getValues,
        trigger,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            pixel_id: edit?.pixel_id || '',
            access_token: '',
        }
    })

    // O formato válido do ID depende da plataforma, então trocar a plataforma
    // com o campo já preenchido precisa revalidar — senão o erro exibido é o da
    // plataforma anterior. Campo vazio fica de fora para não acusar "obrigatório"
    // antes de o produtor ter tido a chance de digitar.
    function changePlatform(platform) {
        setSelectedPlatform(platform)
        setPlatformError(false)

        if (getValues('pixel_id')) {
            trigger('pixel_id')
        }
    }

    function toggleEvent(key) {
        setSelectedEvents(previous =>
            previous.includes(key) ? previous.filter(item => item !== key) : [...previous, key]
        )
    }

    function toggleAllEvents() {
        setSelectedEvents(allSelected ? [] : trackingEvents.map(item => item.key))
    }

    function createPayload(content) {
        return {
            platform: selectedPlatform?.value,
            pixel_id: content.pixel_id,
            is_active: active,
            tracking_events: selectedEvents,
            ...accessTokenPayload(content.access_token),
        }
    }

    /**
     * A API nunca devolve o token, então o campo nasce vazio mesmo quando há um
     * salvo. Vazio significa "não mexe"; `null` é o pedido explícito de apagar.
     */
    function accessTokenPayload(value) {
        if (removeToken) {
            return { access_token: null }
        }

        const token = (value || '').trim()

        return token ? { access_token: token } : {}
    }

    async function onSubmit(content) {
        if (!selectedPlatform) {
            setPlatformError(true)
            return
        }

        setPlatformError(false)
        setSubmitError('')
        setLoading(true)

        try {
            const payload = createPayload(content)

            if (edit) {
                await pixelsApi.update(event.slug, edit.id, payload)
                onSuccess('editPixel')
            } else {
                await pixelsApi.create(event.slug, payload)
                onSuccess('createPixel')
            }
        } catch (error) {
            setSubmitError(firstApiError(error))
        } finally {
            setLoading(false)
        }
    }

    /**
     * A API valida o formato do ID por plataforma e devolve a explicação em
     * `errors.pixel_id`. Mostrar essa mensagem em vez de um texto genérico é o
     * que diz ao produtor o que corrigir.
     */
    function firstApiError(error) {
        const fieldErrors = error?.response?.data?.errors

        if (fieldErrors) {
            const firstField = Object.values(fieldErrors)[0]

            if (firstField?.length) {
                return firstField[0]
            }
        }

        return error?.response?.data?.message || submitErrorMessage
    }

    /* Resumo do que será salvo. A configuração é espalhada por quatro blocos e o
       produtor só descobria o resultado depois de fechar a modal. */
    const summary = (() => {
        const pixelId = watch('pixel_id')

        if (!selectedPlatform || !pixelId) {
            return null
        }

        const count = selectedEvents.length
        const events = count > 0
            ? `${count} evento${count > 1 ? 's' : ''} de conversão`
            : 'somente visitas à página'

        const serverSide = supportsConversionApi && (watch('access_token') || (edit?.has_access_token && !removeToken))
            ? ' · envio server-side'
            : ''

        return `${selectedPlatform.label} · ${pixelId} · ${events}${serverSide}`
    })()

    return (
        <Modal isOpen={visible} onClose={() => setVisible(false)} size='medium'>
            <ModalHeader>
                <ModalTitle>{edit ? 'Editar pixel' : 'Novo pixel de rastreamento'}</ModalTitle>
                <ModalSubtitle>
                    O pixel roda na página pública do evento e envia as conversões para a sua
                    conta de anúncios.
                </ModalSubtitle>
            </ModalHeader>

            {loadingEvents ? (
                <FullyCentered height='160px'>
                    <Loader />
                </FullyCentered>
            ) : (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Field>
                        <StyledLabel>Plataforma</StyledLabel>
                        <PlatformGrid role='radiogroup' aria-label='Plataforma do pixel'>
                            {platforms.map(platform => (
                                <PlatformCard
                                    key={platform.value}
                                    type='button'
                                    role='radio'
                                    aria-checked={selectedPlatform?.value === platform.value}
                                    $selected={selectedPlatform?.value === platform.value}
                                    onClick={() => changePlatform(platform)}
                                >
                                    <PlatformDot color={platform.color} />
                                    <PlatformName>{platform.label}</PlatformName>
                                    <PlatformHint>{platform.hint}</PlatformHint>
                                    {selectedPlatform?.value === platform.value && (
                                        <SelectedMark>
                                            <Check size={12} strokeWidth={3} />
                                        </SelectedMark>
                                    )}
                                </PlatformCard>
                            ))}
                        </PlatformGrid>
                        {platformError && (
                            <FieldError role='alert'>
                                <AlertCircle size={13} />
                                Escolha a plataforma do pixel
                            </FieldError>
                        )}
                    </Field>

                    <Field $spaced>
                        <StyledLabel>ID do pixel</StyledLabel>
                        <Fieldset
                            layout='squired'
                            placeholder={selectedPlatform?.idPlaceholder || 'Escolha a plataforma primeiro'}
                            register={register}
                            name='pixel_id'
                            disabled={!selectedPlatform}
                            validations={fieldValidations.pixelId(selectedPlatform?.value)}
                            error={errors?.pixel_id?.message}
                        />
                        {selectedPlatform && !errors?.pixel_id && (
                            <Hint>{selectedPlatform.idFormat}</Hint>
                        )}
                    </Field>

                    <Field $spaced>
                        <EventsHeader>
                            <StyledLabel>Eventos de conversão</StyledLabel>
                            <LinkButton type='button' onClick={toggleAllEvents}>
                                {allSelected ? 'Limpar' : 'Selecionar todos'}
                            </LinkButton>
                        </EventsHeader>

                        <EventList>
                            {trackingEvents.map(item => {
                                const checked = selectedEvents.includes(item.key)

                                return (
                                    <EventRow key={item.key} $selected={checked}>
                                        <EventCheckbox
                                            type='checkbox'
                                            id={`evt-${item.key}`}
                                            checked={checked}
                                            onChange={() => toggleEvent(item.key)}
                                        />
                                        <EventText htmlFor={`evt-${item.key}`}>
                                            <EventName>{item.label}</EventName>
                                            {item.description && (
                                                <EventDescription>{item.description}</EventDescription>
                                            )}
                                        </EventText>
                                    </EventRow>
                                )
                            })}
                        </EventList>

                        {selectedEvents.length === 0 && (
                            <Notice>
                                <AlertCircle size={14} aria-hidden='true' />
                                <span>
                                    Sem nenhum evento marcado o pixel só registra visitas à página —
                                    a campanha não consegue otimizar por venda.
                                </span>
                            </Notice>
                        )}
                    </Field>

                    {supportsConversionApi && (
                        <Field $spaced>
                            <StyledLabel>Token da Conversions API</StyledLabel>
                            <Fieldset
                                layout='squired'
                                placeholder={edit?.has_access_token ? 'Token salvo — preencha para substituir' : 'Opcional'}
                                register={register}
                                name='access_token'
                                inputType='password'
                                badgeIconPassword
                                badgeIconColorPassword={colors.night}
                                badgeIconPositionPassword='right'
                                disabled={removeToken}
                                validations={fieldValidations.accessToken()}
                                error={errors?.access_token?.message}
                            />
                            <Hint>
                                Com o token, a compra também é enviada do nosso servidor. É o que
                                recupera as vendas no pix pagas depois de o comprador fechar a página.
                            </Hint>

                            {edit?.has_access_token && (
                                <TokenStatus $removing={removeToken}>
                                    <ShieldCheck size={14} aria-hidden='true' />
                                    <span>
                                        {removeToken
                                            ? 'O token salvo será removido ao salvar.'
                                            : 'Há um token configurado para este pixel.'}
                                    </span>
                                    <LinkButton
                                        type='button'
                                        onClick={() => setRemoveToken(previous => !previous)}
                                    >
                                        {removeToken ? 'Desfazer' : 'Remover'}
                                    </LinkButton>
                                </TokenStatus>
                            )}
                        </Field>
                    )}

                    <SwitchCard>
                        <Switch
                            label='Pixel ativo'
                            labelSize={fonts.sizes.default}
                            onChange={(value) => setActive(value)}
                            checked={active}
                        />
                        <SwitchHint>
                            {active
                                ? 'Passa a rastrear na página do evento assim que você salvar.'
                                : 'Fica salvo, mas não carrega na página nem envia conversão.'}
                        </SwitchHint>
                    </SwitchCard>

                    {summary && (
                        <Summary>
                            <Crosshair size={15} strokeWidth={2.5} aria-hidden='true' />
                            <span>{summary}</span>
                        </Summary>
                    )}

                    {submitError && (
                        <FormError role='alert'>
                            <AlertCircle size={14} aria-hidden='true' />
                            {submitError}
                        </FormError>
                    )}

                    {loading ? (
                        <FullyCentered height='58px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <ButtonsContainer>
                            <Button variant='light' category='roundedBorded' onClick={() => setVisible(false)}>
                                Cancelar
                            </Button>
                            <Button variant='featured' category='roundedBorded' type='submit'>
                                {edit ? 'Salvar alterações' : 'Adicionar pixel'}
                            </Button>
                        </ButtonsContainer>
                    )}
                </Form>
            )}
        </Modal>
    )
}

export default PixelForm

/* ─────────────────────────────  Estilos  ───────────────────────────── */

const ModalHeader = styled.header`
    text-align: left;
    padding-right: 28px;
`

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
`

const ModalSubtitle = styled.p`
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.night};
`

const Form = styled.form`
    width: 100%;
    margin-top: 18px;
    text-align: left;
`

const Field = styled.div`
    width: 100%;
    text-align: left;

    ${({ $spaced }) => $spaced && `margin-top: 16px;`}
`

const Hint = styled.p`
    margin: 6px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: ${colors.night};
`

const FieldError = styled.p`
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 6px 0 0;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.dangerDark};
`

/* Três opções fixas: cartões resolvem em um clique o que o select resolvia em
   dois, e ainda mostram a cor da plataforma, que é como o produtor a reconhece
   na listagem. */
const PlatformGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 6px;
`

const PlatformCard = styled.button`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 12px 10px;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected }) => ($selected ? colors.occasionalPurple : '#D4D4D4')};
    background: ${({ $selected }) => ($selected ? '#F7EFFF' : colors.neutralLight)};
    cursor: pointer;
    text-align: left;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
        border-color: ${({ $selected }) => ($selected ? colors.occasionalPurple : colors.nightLight)};
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${colors.occasionalPurple}1A;
    }
`

const PlatformDot = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color};
`

const PlatformName = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
`

const PlatformHint = styled.span`
    font-size: 10px;
    line-height: 1.3;
    color: ${colors.night};
`

const SelectedMark = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    display: flex;
    align-items: center;
    justify-content: center;
`

const EventsHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`

const LinkButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.secondaryPurple};
    cursor: pointer;

    &:hover { text-decoration: underline; }
    &:focus-visible { outline: 1px solid ${colors.occasionalPurple}; outline-offset: 2px; }
`

const EventList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;
`

const EventRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid ${({ $selected }) => ($selected ? '#DFC8F8' : '#E2E4EA')};
    background: ${({ $selected }) => ($selected ? '#F7EFFF' : colors.neutralLight)};
    transition: border-color 0.15s ease, background 0.15s ease;
`

const EventCheckbox = styled.input`
    width: 16px;
    height: 16px;
    margin-top: 1px;
    flex-shrink: 0;
    cursor: pointer;
    accent-color: ${colors.occasionalPurple};
`

const EventText = styled.label`
    display: flex;
    flex-direction: column;
    gap: 1px;
    cursor: pointer;
`

const EventName = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
`

const EventDescription = styled.span`
    font-size: 11px;
    line-height: 1.35;
    color: ${colors.night};
`

const Notice = styled.p`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 10px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fff8e6;
    border: 1px solid #f2dfae;
    color: #8a6100;
    font-size: 11px;
    line-height: 1.4;

    svg { flex-shrink: 0; margin-top: 1px; }
`

const TokenStatus = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 8px 0 0;
    font-size: 11px;
    font-weight: 600;
    color: ${({ $removing }) => ($removing ? colors.dangerDark : colors.night)};

    svg { flex-shrink: 0; }
`

const SwitchCard = styled.div`
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid #e2e4ea;
    background: ${colors.neutralBackground};
`

const SwitchHint = styled.p`
    margin: 4px 0 0;
    font-size: 11px;
    color: ${colors.night};
`

const Summary = styled.p`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 14px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f7efff;
    border: 1px solid #dfc8f8;
    color: #6d14cc;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    word-break: break-word;

    svg { flex-shrink: 0; }
`

const FormError = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 14px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fdf0f1;
    border: 1px solid #f4d3d6;
    color: ${colors.dangerDark};
    font-size: 12px;
    font-weight: 600;
`

const ButtonsContainer = styled(Column)`
    margin-top: 20px;

    @media screen and (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        justify-content: flex-end;
        gap: 8px;

        & > button {
            width: auto;
        }
    }
`
